import React from 'react';

import styled from 'styled-components';

import CtaButton from 'components/CtaButton/CtaButton';
import InnerHtml from 'components/InnerHtml/InnerHtml';

import useWindowDimensions from 'hooks/useWindowDimensions';

import { AnimatedCardCta } from 'interfaces/content-stack/modular-blocks/animatedCards';

import { autoOptimizeImage } from 'utils/functions';
import { getModularBlockLinkData } from 'utils/modularBlockUtils';

const CtaSectionContainer = styled.div`
  z-index: 10;
  gap: 1.25rem;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 1rem;
  display: flex;
  grid-area: span 1 / span 1 / span 1 / span 1;

  @media screen and (max-width: ${(props) => props.theme.breakpoints.lg}) {
    grid-area: span 1 / span 2 / span 1 / span 2;
    text-align: center;
    justify-content: flex-start;
    align-items: center;
  }

  @media screen and (max-width: ${(props) => props.theme.breakpoints.md}) {
    align-items: stretch;
  }
`;

const HeaderContainer = styled.div`
  flex-flow: column;
  justify-content: flex-start;
  display: flex;

  @media screen and (max-width: ${(props) => props.theme.breakpoints.lg}) {
    align-items: center;
  }

  h2 {
    font-size: 3rem;
    text-transform: uppercase;
    margin-top: 0;
    margin-bottom: 0;
    font-weight: 500;
    line-height: 1.6;

    @media screen and (max-width: ${(props) => props.theme.breakpoints.lg}) {
      font-size: 2.75rem;
    }

    @media screen and (max-width: ${(props) => props.theme.breakpoints.sm}) {
      font-size: 2rem;
    }
  }
`;

const HeaderBadge = styled.img`
  width: 12rem;
  height: 12rem;
  margin-top: 0.5rem;
  margin-bottom: 1.5rem;
`;

const TextContainer = styled.div`
  grid-column-gap: 2rem;
  grid-row-gap: 2rem;
  flex-flow: column;
  justify-content: flex-start;
  align-self: flex-start;
  align-items: flex-start;
  display: flex;

  @media screen and (max-width: ${(props) => props.theme.breakpoints.lg}) {
    justify-content: flex-start;
    align-items: center;
  }

  @media screen and (max-width: ${(props) => props.theme.breakpoints.md}) {
    align-self: auto;
  }

  @media screen and (max-width: ${(props) => props.theme.breakpoints.xs}) {
    align-items: normal;
  }
`;

const DescriptionText = styled.div`
  font-size: 1.25rem;

  @media screen and (max-width: ${(props) => props.theme.breakpoints.md}) {
    font-size: 1.15rem;
  }
`;

const ButtonContainer = styled.div`
  gap: 1rem;
  flex-direction: row;
  align-items: center;
  display: flex;

  @media (max-width: ${(props) => props.theme.breakpoints.xs}) {
    flex-direction: column;
    gap: 1.5rem;
  }
`;

const DisclaimerText = styled.div`
  font-size: 0.75rem;
`;

const CtaSection: React.FC<{ data: AnimatedCardCta }> = ({ data }) => {
  // In webflow, linebreaks are removed at some screen sizes. The only way I
  // could find to do this would be to replace them with JS. display: none does
  // remove the linebreaks, but there's no way to replace it with a space, so
  // everything runs together.
  const { width } = useWindowDimensions();

  return (
    <CtaSectionContainer>
      <HeaderContainer>
        {data.image?.imageAsset && (
          <HeaderBadge src={autoOptimizeImage(data.image.imageAsset.url)} alt={data.image.altImageText || ''} />
        )}
        {data.header && <InnerHtml richText={width < 992 ? data.header.replaceAll(/<br[^>]*>/gi, ' ') : data.header} />}
      </HeaderContainer>
      <TextContainer>
        <DescriptionText>
          {data.description && <InnerHtml fontWeight='500' richText={data.description} />}
        </DescriptionText>
        <ButtonContainer>
          {data.buttonCta.map((button, index) => {
            const { link, openInNewWindow } = getModularBlockLinkData(button.buttonLink);

            return (
              <CtaButton
                key={index}
                to={link}
                buttonType={button.buttonType}
                text={button.buttonText}
                openInNewWindow={openInNewWindow}
                faint
              />
            );
          })}
        </ButtonContainer>
        <DisclaimerText>{data.disclaimer && <InnerHtml fontWeight='500' richText={data.disclaimer} />}</DisclaimerText>
      </TextContainer>
    </CtaSectionContainer>
  );
};

export default CtaSection;
